const isWithinOfferDates = (refDate, start, end) => {
  const refDay = new Date(refDate);
  refDay.setHours(0, 0, 0, 0);

  const startDay = new Date(start);
  startDay.setHours(0, 0, 0, 0);

  const endDay = new Date(end);
  endDay.setHours(23, 59, 59, 999);

  return !(refDay < startDay || refDay > endDay);
};

const getPrestationCategoryIds = (prestation) => {
  const ids = new Set();
  if (prestation?.categorie_id) ids.add(String(prestation.categorie_id).trim());
  if (prestation?.categorie?.id) ids.add(String(prestation.categorie.id).trim());
  if (Array.isArray(prestation?.categories)) {
    prestation.categories.forEach((entry) => {
      const id = entry?.categorie?.id || entry?.id || null;
      if (id) ids.add(String(id).trim());
    });
  }
  return Array.from(ids);
};

const getCategoryLabelMap = (prestations = []) => {
  const labels = new Map();
  (prestations || []).forEach((prestation) => {
    if (prestation?.categorie_id && prestation?.categorie?.libelle) {
      labels.set(String(prestation.categorie_id).trim(), prestation.categorie.libelle);
    }
    if (Array.isArray(prestation?.categories)) {
      prestation.categories.forEach((entry) => {
        const categoryId = entry?.categorie?.id || entry?.id || null;
        const categoryLabel = entry?.categorie?.libelle || entry?.libelle || null;
        if (categoryId && categoryLabel) {
          labels.set(String(categoryId).trim(), categoryLabel);
        }
      });
    }
  });
  return labels;
};

const getEligibleCategoryPresentation = (eligibleCategoryIds = [], prestations = []) => {
  if (!Array.isArray(eligibleCategoryIds) || eligibleCategoryIds.length === 0) {
    return {
      categorieId: null,
      categorieLabels: ['Toutes categories'],
      categorieTargets: [],
      categorieLabel: 'Toutes categories',
    };
  }

  const labelMap = getCategoryLabelMap(prestations);
  const categorieTargets = eligibleCategoryIds
    .map((id) => {
      const normalizedId = String(id).trim();
      const label = labelMap.get(normalizedId) || null;
      if (!label) return null;
      return { id: normalizedId, label };
    })
    .filter(Boolean);
  const categorieLabels = categorieTargets.map((entry) => entry.label);

  return {
    categorieId: eligibleCategoryIds[0] || null,
    categorieLabels,
    categorieTargets,
    categorieLabel: categorieLabels.join(', '),
  };
};

const isSelectablePrestation = (prestation) => {
  const duration = parseInt(prestation?.duree_min, 10);
  return !(Number.isFinite(duration) && duration === 0);
};

const matchesEligibleCategories = (prestation, eligibleCategoryIds = []) => {
  if (!Array.isArray(eligibleCategoryIds) || eligibleCategoryIds.length === 0) return true;
  const prestationCategoryIds = getPrestationCategoryIds(prestation);
  return prestationCategoryIds.some((id) => eligibleCategoryIds.includes(String(id).trim()));
};

const getComparablePrice = (prestation, applicationTarif = 'public') => {
  const publicPrice = Number(prestation?.prix_centimes_public);
  const abonnePrice = Number(prestation?.prix_centimes_abonne);

  if (applicationTarif === 'abonne' && Number.isFinite(abonnePrice)) {
    return abonnePrice;
  }
  if (applicationTarif === 'tous') {
    if (Number.isFinite(publicPrice)) return publicPrice;
    if (Number.isFinite(abonnePrice)) return abonnePrice;
  }
  if (Number.isFinite(publicPrice)) return publicPrice;
  if (Number.isFinite(abonnePrice)) return abonnePrice;
  return Number.MAX_SAFE_INTEGER;
};

const pickCheapestPrestations = (prestations = [], offeredCount = 0, applicationTarif = 'public') =>
  [...prestations]
    .sort((left, right) => {
      const priceDiff =
        getComparablePrice(left, applicationTarif) - getComparablePrice(right, applicationTarif);
      if (priceDiff !== 0) return priceDiff;
      return String(left?.id || '').localeCompare(String(right?.id || ''));
    })
    .slice(0, Math.max(offeredCount, 0));

const normalizeTargetPrestationIds = (prestationIds = []) =>
  Array.from(
    new Set(
      (Array.isArray(prestationIds) ? prestationIds : [])
        .map((id) => String(id).trim())
        .filter((id) => id.length > 0)
    )
  );

export const getOfferInfoFromOffres = (offre, prestations, refDate = new Date()) => {
  if (!offre || !offre.actif) return null;

  const ref = refDate instanceof Date ? refDate : new Date(refDate);
  if (Number.isNaN(ref.getTime())) return null;

  const start = offre.date_debut ? new Date(offre.date_debut) : null;
  const end = offre.date_fin ? new Date(offre.date_fin) : null;

  if (start && Number.isNaN(start.getTime())) return null;
  if (end && Number.isNaN(end.getTime())) return null;

  if (start && end && !isWithinOfferDates(ref, start, end)) return null;

  const details = offre.details || {};
  const conditions = details.conditions || {};
  const eligibleCategoryIds = Array.isArray(conditions.categorie_ids)
    ? conditions.categorie_ids.map((id) => String(id).trim())
    : [];
  const categoryPresentation = getEligibleCategoryPresentation(eligibleCategoryIds, prestations);

  let matchedCategory = null;
  if (Array.isArray(prestations) && prestations.length > 0) {
    if (eligibleCategoryIds.length === 0) {
      matchedCategory = { id: null, libelle: 'Toutes categories' };
    } else {
      for (const prestation of prestations) {
        if (!matchesEligibleCategories(prestation, eligibleCategoryIds)) continue;
        matchedCategory = {
          id: getPrestationCategoryIds(prestation)[0] || null,
          libelle: prestation?.categorie?.libelle || prestation?.categories?.[0]?.categorie?.libelle || null,
        };
        break;
      }
    }
  }

  if (eligibleCategoryIds.length > 0 && !matchedCategory) {
    return null;
  }

  const benefice = details.benefice || {};
  const message =
    (offre.message_client || '').trim() ||
    (details.affichage?.texte || details.affichage?.titre || '').trim();

  if (offre.type_offre === 'n_plus_1') {
    const seuil = parseInt(details.mecanique?.seuil, 10);
    if (!Number.isFinite(seuil) || seuil < 1) return null;

    const offrePrestationId = benefice.prestation_id || null;
    if (!offrePrestationId) return null;

    return {
      source: 'offres',
      typeOffre: offre.type_offre,
      message: message || `1 reservation offerte toutes les ${seuil} reservations payees.`,
      start,
      end,
      seuil,
      categorieId: categoryPresentation.categorieId,
      categorieLabels: categoryPresentation.categorieLabels,
      categorieTargets: categoryPresentation.categorieTargets,
      categorieLabel: categoryPresentation.categorieLabel || matchedCategory?.libelle || null,
      eligibleCategoryIds,
      offrePrestationId,
      details,
      raw: offre,
    };
  }

  if (offre.type_offre === 'lot_moins_chere_offerte') {
    const seuil = parseInt(details.mecanique?.seuil, 10);
    const applicationTarif = benefice.application_tarif || 'public';
    if (!Number.isFinite(seuil) || seuil < 2) return null;
    if (!['public', 'abonne', 'tous'].includes(applicationTarif)) return null;

    return {
      source: 'offres',
      typeOffre: offre.type_offre,
      message: message || `A partir de ${seuil} prestations, la moins chere est offerte.`,
      start,
      end,
      seuil,
      applicationTarif,
      categorieId: categoryPresentation.categorieId,
      categorieLabels: categoryPresentation.categorieLabels,
      categorieTargets: categoryPresentation.categorieTargets,
      categorieLabel: categoryPresentation.categorieLabel || matchedCategory?.libelle || null,
      eligibleCategoryIds,
      details,
      raw: offre,
    };
  }

  if (offre.type_offre === 'tarif_preferentiel_prestation') {
    const targetPrestationIds = normalizeTargetPrestationIds(conditions.prestation_ids);

    if (targetPrestationIds.length === 0) return null;

    const hasMatch =
      Array.isArray(prestations) &&
      prestations.some((p) => targetPrestationIds.includes(String(p?.id).trim()));

    if (!hasMatch) return null;

    return {
      source: 'offres',
      typeOffre: offre.type_offre,
      message: message || 'Tarif preferentiel',
      start,
      end,
      targetPrestationIds,
      details,
      raw: offre,
    };
  }

  if (offre.type_offre === 'lot_prestations_prix_fixe') {
    const targetPrestationIds = normalizeTargetPrestationIds(conditions.prestation_ids);
    if (targetPrestationIds.length < 2) return null;

    const matchedTargetPrestationIds = Array.isArray(prestations)
      ? prestations
          .map((prestation) => String(prestation?.id || '').trim())
          .filter((id) => targetPrestationIds.includes(id))
      : [];

    if (matchedTargetPrestationIds.length === 0) return null;

    return {
      source: 'offres',
      typeOffre: offre.type_offre,
      message: message || 'Pack prestations',
      start,
      end,
      targetPrestationIds,
      matchedTargetPrestationIds,
      details,
      raw: offre,
    };
  }

  return {
    source: 'offres',
    typeOffre: offre.type_offre,
    message: message || '',
    start,
    end,
    details,
    raw: offre,
    categorieId: categoryPresentation.categorieId,
    categorieLabels: categoryPresentation.categorieLabels,
    categorieTargets: categoryPresentation.categorieTargets,
    categorieLabel: categoryPresentation.categorieLabel || matchedCategory?.libelle || null,
    eligibleCategoryIds,
  };
};

export const getOfferSelectionState = (
  selectedPrestations,
  activeOffre = null,
  refDate = new Date()
) => {
  const offersList = Array.isArray(activeOffre) ? activeOffre : activeOffre ? [activeOffre] : [];
  const offeredIds = new Set();
  const autoAddedOfferedIds = new Set();
  const appliedOffers = [];

  let primaryOfferInfo = null;
  let primaryCountPaid = 0;
  let primaryRemaining = null;
  let primaryCounter = '';

  const setPrimary = (info, metrics = {}) => {
    primaryOfferInfo = info;
    primaryCountPaid = metrics.countPaid ?? 0;
    primaryRemaining = metrics.remaining ?? null;
    primaryCounter = metrics.counter ?? '';
  };

  for (const offre of offersList) {
    const info = getOfferInfoFromOffres(offre, selectedPrestations, refDate);
    if (!info) continue;

    if (info.typeOffre === 'n_plus_1' && info.offrePrestationId) {
      const eligiblePrestations = (selectedPrestations || []).filter((prestation) => {
        if (!isSelectablePrestation(prestation)) return false;
        if (!matchesEligibleCategories(prestation, info.eligibleCategoryIds)) return false;
        if (offeredIds.has(String(prestation?.id))) return false;
        return String(prestation?.id) !== String(info.offrePrestationId);
      });

      const countPaid = eligiblePrestations.length;
      const remaining = Math.max(info.seuil - countPaid, 0);
      const isActive = remaining === 0;
      const counter = isActive
        ? 'La prestation offerte est ajoutee.'
        : `Plus que ${remaining} ${remaining > 1 ? 'prestations' : 'prestation'}${
            info.categorieLabel ? ` ${info.categorieLabel}` : ''
          } avant de beneficier de l'offre.`;

      if (isActive) {
        offeredIds.add(String(info.offrePrestationId));
        autoAddedOfferedIds.add(String(info.offrePrestationId));
      }

      appliedOffers.push({
        ...info,
        type: 'n_plus_1',
        active: isActive,
        remaining,
        offeredPrestationIds: isActive ? [String(info.offrePrestationId)] : [],
        autoAddedOfferedIds: isActive ? [String(info.offrePrestationId)] : [],
      });

      if (!primaryOfferInfo || primaryOfferInfo.typeOffre !== 'n_plus_1') {
        setPrimary(info, { countPaid, remaining, counter });
      }
      continue;
    }

    if (info.typeOffre === 'lot_moins_chere_offerte') {
      const eligiblePrestations = (selectedPrestations || []).filter(
        (prestation) =>
          isSelectablePrestation(prestation) &&
          matchesEligibleCategories(prestation, info.eligibleCategoryIds)
      );

      const countPaid = eligiblePrestations.length;
      const remaining = Math.max(info.seuil - countPaid, 0);
      const offeredCount = remaining === 0 ? Math.floor(countPaid / info.seuil) : 0;
      const discountedPrestations = pickCheapestPrestations(
        eligiblePrestations,
        offeredCount,
        info.applicationTarif
      );
      const discountedPrestationIds = discountedPrestations.map((prestation) =>
        String(prestation?.id)
      );
      const isActive = discountedPrestationIds.length > 0;
      const counter = isActive
        ? `La moins chere est offerte sur le tarif ${info.applicationTarif}.`
        : `Plus que ${remaining} ${remaining > 1 ? 'prestations' : 'prestation'}${
            info.categorieLabel ? ` ${info.categorieLabel}` : ''
          } avant de beneficier de l'offre.`;

      appliedOffers.push({
        ...info,
        type: 'lot_moins_chere_offerte',
        active: isActive,
        remaining,
        discountedPrestationIds,
        applicationTarif: info.applicationTarif,
      });

      if (!primaryOfferInfo) {
        setPrimary(info, { countPaid, remaining, counter });
      }
      continue;
    }

    if (info.typeOffre === 'remise_pourcent_categorie') {
      appliedOffers.push({ ...info, type: 'remise_pourcent_categorie', active: true, remaining: 0 });
      if (!primaryOfferInfo) {
        setPrimary(info, { countPaid: 0, remaining: 0, counter: '' });
      }
      continue;
    }

    if (info.typeOffre === 'lot_prestations_prix_fixe') {
      const selectedIds = new Set((selectedPrestations || []).map((prestation) => String(prestation?.id || '').trim()));
      const matchedTargetPrestationIds = info.targetPrestationIds.filter((id) => selectedIds.has(id));
      const remaining = Math.max(info.targetPrestationIds.length - matchedTargetPrestationIds.length, 0);
      const isActive = remaining === 0;
      const counter = isActive
        ? 'Le tarif pack est applique.'
        : `Plus que ${remaining} ${remaining > 1 ? 'prestations' : 'prestation'} du lot avant d'activer l'offre.`;

      appliedOffers.push({
        ...info,
        type: 'lot_prestations_prix_fixe',
        active: isActive,
        remaining,
        matchedTargetPrestationIds,
      });

      if (!primaryOfferInfo) {
        setPrimary(info, {
          countPaid: matchedTargetPrestationIds.length,
          remaining,
          counter,
        });
      }
      continue;
    }

    if (info.typeOffre === 'tarif_preferentiel_prestation') {
      appliedOffers.push({ ...info, type: 'tarif_preferentiel_prestation', active: true, remaining: 0 });
      if (!primaryOfferInfo) {
        setPrimary(info, { countPaid: 0, remaining: 0, counter: '' });
      }
    }
  }

  if (appliedOffers.length === 0) {
    return {
      offerInfo: null,
      appliedOffers: [],
      offeredIds: new Set(),
      autoAddedOfferedIds: new Set(),
      countPaid: 0,
      remaining: null,
      counter: '',
    };
  }

  return {
    offerInfo: primaryOfferInfo,
    appliedOffers,
    offeredIds,
    autoAddedOfferedIds,
    countPaid: primaryCountPaid,
    remaining: primaryRemaining,
    counter: primaryCounter,
  };
};
